/**
 * Cadastros: CRUD sobre os recursos do Traccar.
 *
 * Todo recurso do Traccar segue o mesmo contrato (`BaseObjectResource`):
 *   GET    /api/<recurso>            lista o que o usuário enxerga
 *   GET    /api/<recurso>?deviceId=N lista o que está VINCULADO ao veículo
 *   POST   /api/<recurso>            cria
 *   PUT    /api/<recurso>/{id}       substitui — o objeto vai INTEIRO
 *   DELETE /api/<recurso>/{id}       remove
 *
 * ⚠️ O PUT substitui. Editar dois campos e mandar só eles apaga o resto,
 * inclusive `attributes`. Por isso o formulário edita uma CÓPIA do objeto
 * carregado e devolve o objeto inteiro.
 *
 * Vínculo é `permission`, não campo: POST/DELETE em `/api/permissions` com
 * `{deviceId, geofenceId}` e afins. E só se consulta no sentido veículo →
 * recurso: não existe endpoint que liste os veículos de uma cerca. É por isso
 * que os vínculos são editados a partir do veículo (o traccar-web faz igual).
 */
import { traccar, type Device, type Motorista, type Geofence, type Manutencao } from "./traccar";

export type Id = number;

/**
 * O grupo é o cadastro do cliente dono da frota (ADR-0012). Os campos abaixo de
 * `attributes` são colunas de verdade, gravadas pelo próprio Traccar.
 *
 * `diaVencimento` e `mensalidade` são primitivos no model e nunca chegam nulos:
 * vazio vem como 0.
 */
export interface Grupo {
  id: Id; name: string; groupId: number; attributes: Attrs;
  razaoSocial?: string; nomeFantasia?: string; cpfCnpj?: string;
  inscricaoEstadual?: string;
  contato?: string; telefone?: string; celular?: string; email?: string;
  cep?: string; logradouro?: string; numero?: string; complemento?: string;
  bairro?: string; cidade?: string; uf?: string;
  indicacao?: string; vendedor?: string; contrato?: string;
  inicioContrato?: string | null; diaVencimento?: number; mensalidade?: number;
  situacao?: string; observacoes?: string;
  /** Nome do arquivo na pasta de mídia — a URL sai de `urlFoto`. */
  foto?: string;
}
export interface Usuario {
  id: Id; name: string; email: string; phone?: string;
  readonly: boolean; administrator: boolean; disabled: boolean;
  deviceLimit: number; expirationTime?: string | null;
  password?: string; attributes: Attrs;
  cpf?: string; nascimento?: string | null; celular?: string;
  contatoEmergencia?: string; telefoneEmergencia?: string;
  cargo?: string; departamento?: string; matricula?: string;
  centroCusto?: string; admissao?: string | null;
  /**
   * Segredo compartilhado para conferir identidade por telefone. Fica legível
   * de propósito — quem atende precisa ler. NÃO é credencial de acesso.
   */
  fraseSecreta?: string;
  observacoes?: string;
  /** Nome do arquivo na pasta de mídia — a URL sai de `urlFoto`. */
  foto?: string;
}
export interface Notificacao {
  id: Id; type: string; always: boolean; notificators: string;
  calendarId: number; commandId: number; attributes: Attrs;
}
export interface ComandoSalvo {
  id: Id; description: string; type: string; textChannel: boolean; attributes: Attrs;
}
export interface AtributoCalculado {
  id: Id; description: string; attribute: string; expression: string;
  type: "string" | "number" | "boolean"; attributes: Attrs;
}
export interface Calendario { id: Id; name: string; data: string; attributes: Attrs; }

/**
 * Relatório agendado.
 *
 * O calendário é quem manda: o servidor roda o relatório ao FIM de cada
 * período do calendário e manda por e-mail para os usuários vinculados. Sem
 * calendário não há agendamento.
 */
export interface RelatorioAgendado {
  id: Id; type: string; description: string; calendarId: number; attributes: Attrs;
}

export type Attrs = Record<string, unknown>;
export type Entidade =
  | Device | Grupo | Motorista | Geofence | Manutencao
  | Notificacao | Usuario | ComandoSalvo | AtributoCalculado | Calendario
  | RelatorioAgendado;

export const RECURSOS = {
  devices: "/devices",
  groups: "/groups",
  drivers: "/drivers",
  geofences: "/geofences",
  maintenance: "/maintenance",
  notifications: "/notifications",
  users: "/users",
  commands: "/commands",
  computed: "/attributes/computed",
  calendars: "/calendars",
  reports: "/reports",
} as const;
export type Recurso = keyof typeof RECURSOS;

/** Quem é o dono da permission: os três lados que a API sabe consultar. */
export type ChaveDono = "deviceId" | "groupId" | "userId";

/** Um lado da permission. `chave` é o nome do campo no corpo da requisição. */
export type Dono = { chave: ChaveDono; id: Id };
export type Objeto = { chave: string; id: Id; recurso: Recurso };

const MOCK = import.meta.env.DEV && import.meta.env.VITE_MOCK !== "0";

// ── fotos de cadastro (ADR-0013) ──────────────────────────────────────────

/**
 * Pasta de mídia por recurso.
 *
 * ⚠️ O veículo é o único que NÃO usa o id: o Traccar guarda a foto dele em
 * `{uniqueId}/`, e é assim desde sempre. Mudar isso deixaria para trás a foto
 * de quem já usa o painel oficial. O efeito colateral é real e vale saber:
 * **trocar o identificador de um veículo deixa a foto para trás**.
 */
const PASTA_FOTO: Partial<Record<Recurso, string>> = {
  groups: "_groups", drivers: "_drivers", users: "_users",
};

/**
 * Contador de revisão para furar o cache do navegador.
 *
 * O arquivo tem sempre o mesmo nome (`photo.png`), então trocar a foto não
 * troca a URL — e logo depois de enviar, o navegador mostraria a antiga. Sobe
 * um degrau a cada envio, e só aí.
 *
 * Entre recarregamentos não é problema: o servidor de mídia manda
 * `Last-Modified`, então o navegador revalida e pega a versão nova sozinho.
 * Usar a hora atual a cada render "resolveria" o mesmo caso derrubando o cache
 * inteiro — uma requisição por foto, por render, para sempre.
 */
let revisaoFoto = 0;

export type ComFoto = { id: Id; foto?: string; uniqueId?: string };

export const urlFoto = (r: Recurso, item: ComFoto): string | null => {
  if (!item.foto) return null;
  const pasta = r === "devices" ? item.uniqueId : `${PASTA_FOTO[r]}/${item.id}`;
  if (!pasta) return null;
  return `/api/media/${pasta}/${item.foto}${revisaoFoto ? `?v=${revisaoFoto}` : ""}`;
};

/**
 * Envia a foto e devolve o NOME do arquivo, que o cadastro guarda na coluna
 * `foto`. O corpo é a imagem crua — é assim que o endpoint do Traccar recebe,
 * e os nossos espelham isso. Nada de multipart.
 */
export const enviarFoto = async (r: Recurso, id: Id, arquivo: File): Promise<string> => {
  if (MOCK) { revisaoFoto++; return "photo.png"; }
  const resposta = await fetch(`/api${RECURSOS[r]}/${id}/image`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": arquivo.type },
    body: arquivo,
  });
  if (!resposta.ok) {
    throw new Error(resposta.status === 400
      ? "Imagem recusada: use JPG, PNG, GIF ou WebP com até 500 KB."
      : `Falha ao enviar a foto (${resposta.status}).`);
  }
  revisaoFoto++;
  if (r === "users") avisarUsuarioMudou();
  return (await resposta.text()).trim();
};

/**
 * Avisa a casca que um usuário mudou.
 *
 * O cabeçalho mostra a foto de quem está logado, e ele lê a sessão uma vez, na
 * entrada. Sem este aviso a foto nova só apareceria no próximo F5 — que é
 * exatamente o tipo de coisa que o operador reporta como "não salvou".
 *
 * Dispara para QUALQUER usuário, não só o da sessão: descobrir se o id mexido é
 * o meu custaria mais do que a casca reler a própria sessão.
 */
export const avisarUsuarioMudou = () =>
  window.dispatchEvent(new CustomEvent("usuario:atualizado"));

export const cadastros = {
  listar: <T>(r: Recurso) =>
    MOCK ? mockListar<T>(r) : traccar.raw<T[]>(RECURSOS[r]),

  /**
   * Tudo que existe do recurso, não só o que o próprio usuário enxerga.
   *
   * É o que a tela de permissões precisa: quem concede acesso tem de ver o que
   * ainda NÃO concedeu. `all=true` só vale para administrador — para os outros
   * o servidor cai de volta no que a pessoa já enxerga, que é o certo.
   */
  listarTudo: <T>(r: Recurso) =>
    MOCK ? mockListar<T>(r) : traccar.raw<T[]>(`${RECURSOS[r]}?all=true`),

  /**
   * O que está vinculado a um dono — veículo, cliente ou usuário.
   *
   * A API só consulta neste sentido: `?deviceId=`, `?groupId=` e `?userId=`
   * existem; o inverso (quais veículos usam esta cerca) não.
   */
  listarDoDono: <T>(r: Recurso, chave: ChaveDono, id: Id) =>
    MOCK ? mockVinculados<T>(r, id)
         : traccar.raw<T[]>(`${RECURSOS[r]}?${chave}=${id}`),

  criar: <T extends { id: Id }>(r: Recurso, item: Omit<T, "id">) =>
    MOCK ? mockCriar<T>(r, item)
         : traccar.raw<T>(RECURSOS[r], { method: "POST", ...corpo(item) }),

  async salvar<T extends { id: Id }>(r: Recurso, item: T) {
    const salvo = MOCK ? await mockSalvar<T>(r, item)
      : await traccar.raw<T>(`${RECURSOS[r]}/${item.id}`, { method: "PUT", ...corpo(item) });
    if (r === "users") avisarUsuarioMudou();
    return salvo;
  },

  excluir: (r: Recurso, id: Id) =>
    MOCK ? mockExcluir(r, id)
         : traccar.raw<void>(`${RECURSOS[r]}/${id}`, { method: "DELETE" }),

  /**
   * Liga ou desliga UMA permission.
   *
   * O corpo tem exatamente duas chaves: o dono e o objeto — `{deviceId,
   * geofenceId}`, `{groupId, driverId}`, `{userId, managedUserId}`. Os dois
   * lados vêm separados de propósito: em `{userId, deviceId}` e em
   * `{deviceId, linkedDeviceId}` não dá para descobrir quem é o dono olhando
   * só o objeto pronto.
   */
  async permissao(dono: Dono, objeto: Objeto, ligar: boolean) {
    const vinculo = { [dono.chave]: dono.id, [objeto.chave]: objeto.id };
    if (MOCK) return mockPermissao(dono, objeto, ligar);
    await traccar.raw<void>("/permissions", {
      method: ligar ? "POST" : "DELETE", ...corpo(vinculo),
    });
  },

  /**
   * Liga ou desliga VÁRIAS permissions de uma vez.
   *
   * `/permissions/bulk` é do próprio Traccar e recebe a lista inteira numa
   * requisição — cem cercas viram uma chamada, não cem. O servidor exige que
   * todos os itens tenham as MESMAS chaves, o que aqui é natural: mesmo dono,
   * mesmo tipo de objeto.
   */
  async permissoesEmLote(dono: Dono, objetos: Objeto[], ligar: boolean) {
    if (!objetos.length) return;
    if (MOCK) {
      for (const o of objetos) await mockPermissao(dono, o, ligar);
      return;
    }
    await traccar.raw<void>("/permissions/bulk", {
      method: ligar ? "POST" : "DELETE",
      ...corpo(objetos.map(o => ({ [dono.chave]: dono.id, [o.chave]: o.id }))),
    });
  },

  /** Catálogos do servidor: tipos de evento, canais, tipos de comando. */
  tiposDeNotificacao: () =>
    MOCK ? Promise.resolve(MOCK_TIPOS_EVENTO.map(type => ({ type })))
         : traccar.raw<{ type: string }[]>("/notifications/types"),
  canais: () =>
    MOCK ? Promise.resolve([{ type: "web" }, { type: "mail" }, { type: "sms" }])
         : traccar.raw<{ type: string }[]>("/notifications/notificators"),
  tiposDeComando: () =>
    MOCK ? Promise.resolve([{ type: "engineStop" }, { type: "engineResume" },
                            { type: "positionSingle" }, { type: "custom" }])
         : traccar.raw<{ type: string }[]>("/commands/types"),
};

const corpo = (item: unknown): RequestInit => ({
  headers: { "content-type": "application/json" },
  body: JSON.stringify(item),
});

// ── rótulos em português ──────────────────────────────────────────────────
export const CANAL: Record<string, string> = {
  web: "Aviso na tela", mail: "E-mail", sms: "SMS", firebase: "Push", telegram: "Telegram",
  // `command` é canal de verdade no Traccar: em vez de avisar alguém, dispara
  // um comando salvo no veículo. Faltava no mapa e aparecia cru na tela.
  command: "Enviar comando ao veículo",
};

/**
 * Tipos de comando do Traccar, em português.
 *
 * A lista vem de `/api/commands/types` e é do SERVIDOR — o que aparece depende
 * do protocolo. Sem esta tabela a tela mostra `engineStop` e `positionSingle`
 * para quem vai bloquear um caminhão.
 *
 * ⚠️ O que não estiver aqui aparece como veio. É de propósito: comando novo
 * numa versão nova do Traccar precisa ser visto para ser traduzido, não
 * escondido atrás de um rótulo genérico.
 */
export const TIPO_COMANDO: Record<string, string> = {
  custom:               "Comando livre",
  deviceIdentification: "Identificar equipamento",
  positionSingle:       "Pedir posição agora",
  positionPeriodic:     "Posição periódica",
  positionStop:         "Parar envio de posição",
  engineStop:           "Bloquear motor",
  engineResume:         "Desbloquear motor",
  alarmArm:             "Armar alarme",
  alarmDisarm:          "Desarmar alarme",
  alarmDismiss:         "Silenciar alarme",
  setTimezone:          "Definir fuso horário",
  requestPhoto:         "Pedir foto",
  powerOff:             "Desligar equipamento",
  rebootDevice:         "Reiniciar equipamento",
  factoryReset:         "Restaurar de fábrica",
  sendSms:              "Enviar SMS",
  sendUssd:             "Enviar USSD",
  sosNumber:            "Número de emergência",
  silenceTime:          "Horário de silêncio",
  setPhonebook:         "Definir agenda telefônica",
  message:              "Mensagem no painel",
  voiceMessage:         "Mensagem de voz",
  outputControl:        "Acionar saída",
  voiceMonitoring:      "Escuta ambiente",
  setAgps:              "Configurar AGPS",
  setIndicator:         "Configurar indicador",
  configuration:        "Configuração",
  getVersion:           "Versão do firmware",
  firmwareUpdate:       "Atualizar firmware",
  setConnection:        "Definir servidor",
  setOdometer:          "Ajustar odômetro",
  getModemStatus:       "Status do modem",
  getDeviceStatus:      "Status do equipamento",
  setSpeedLimit:        "Limite de velocidade",
  modePowerSaving:      "Modo economia",
  modeDeepSleep:        "Modo repouso profundo",
  videoStart:           "Iniciar vídeo",
  videoStop:            "Parar vídeo",
  alarmGeofence:        "Alarme de cerca",
  alarmBattery:         "Alarme de bateria",
  alarmSos:             "Alarme de emergência",
  alarmRemove:          "Alarme de remoção",
  alarmClock:           "Despertador",
  alarmSpeed:           "Alarme de velocidade",
  alarmFall:            "Alarme de queda",
  alarmVibration:       "Alarme de vibração",
};

/** Os relatórios que o Traccar sabe agendar, com nome de gente. */
export const TIPO_RELATORIO: Record<string, string> = {
  route: "Posições", events: "Eventos", trips: "Viagens",
  stops: "Paradas", summary: "Resumo", combined: "Combinado",
};

/** Rótulo de um comando; devolve o próprio tipo quando não há tradução. */
export const rotuloComando = (tipo: string) => TIPO_COMANDO[tipo] ?? tipo;

export const TIPO_MANUTENCAO: { valor: string; rotulo: string; unidade: "km" | "h" }[] = [
  { valor: "totalDistance", rotulo: "Distância acumulada", unidade: "km" },
  { valor: "odometer",      rotulo: "Odômetro",            unidade: "km" },
  { valor: "hours",         rotulo: "Horas de motor",      unidade: "h" },
];

/**
 * Campos de gestão de frota que o Traccar não tem como coluna.
 *
 * Vão em `device.attributes`, que é onde o Traccar guarda o que é do cliente.
 * Os nomes seguem o que o mercado brasileiro usa — placa, RENAVAM, ICCID,
 * APN —, porque é o que o instalador e o gestor procuram.
 *
 * ⚠️ `tc_devices` recebe UPDATE a cada posição (docs/12), então a linha é
 * quente: cada atributo pesa em toda atualização. Por isso a lista é a do que
 * se usa de verdade, não um formulário de tudo que existe.
 */
export const OPERADORAS = ["Vivo", "Claro", "TIM", "Algar", "Arqia", "Linkfields",
                           "Transmit", "Virtueyes", "Outra"];

export const COMBUSTIVEIS = ["Diesel S10", "Diesel S500", "Gasolina", "Etanol",
                             "Flex", "GNV", "Elétrico", "Híbrido"];

export const CORES = ["Branco", "Prata", "Cinza", "Preto", "Vermelho", "Azul",
                      "Verde", "Amarelo", "Marrom", "Bege", "Outra"];

export const UFS = ["AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MA",
                    "MG", "MS", "MT", "PA", "PB", "PE", "PI", "PR", "RJ", "RN",
                    "RO", "RR", "RS", "SC", "SE", "SP", "TO"]
  .map(uf => ({ valor: uf, rotulo: uf }));

/**
 * Situação comercial do cliente. É rótulo, não regra: quem suspende de fato é o
 * control plane, pela cota e pelo pagamento. Aqui serve para a central saber com
 * quem está falando antes de atender.
 */
export const SITUACAO_CLIENTE = [
  { valor: "prospect",    rotulo: "Prospect" },
  { valor: "ativo",       rotulo: "Ativo" },
  { valor: "inadimplente", rotulo: "Inadimplente" },
  { valor: "suspenso",    rotulo: "Suspenso" },
  { valor: "cancelado",   rotulo: "Cancelado" },
];

export const CATEGORIA: { valor: string; rotulo: string }[] = [
  { valor: "default", rotulo: "Padrão" }, { valor: "car", rotulo: "Carro" },
  { valor: "truck", rotulo: "Caminhão" }, { valor: "van", rotulo: "Van" },
  { valor: "bus", rotulo: "Ônibus" },     { valor: "motorcycle", rotulo: "Moto" },
  { valor: "pickup", rotulo: "Picape" },  { valor: "tractor", rotulo: "Trator" },
  { valor: "boat", rotulo: "Embarcação" },{ valor: "person", rotulo: "Pessoa" },
];

/**
 * Conversão de unidade das manutenções.
 * O Traccar guarda distância em METROS e horímetro em MILISSEGUNDOS; a tela
 * fala km e h. Errar aqui produz "troca de óleo a cada 10 mil metros".
 */
export const paraBruto = (tipo: string, valor: number) =>
  tipo === "hours" ? valor * 3_600_000 : valor * 1000;
export const paraTela = (tipo: string, bruto: number) =>
  tipo === "hours" ? bruto / 3_600_000 : bruto / 1000;

// ── loja em memória para `npm run dev` ────────────────────────────────────
const loja = new Map<Recurso, Entidade[]>();
const vinculos = new Map<string, Set<number>>();      // `${recurso}:${deviceId}`
let proximoId = 1000;

async function semear(r: Recurso): Promise<Entidade[]> {
  if (loja.has(r)) return loja.get(r)!;
  const inicial: Entidade[] = r === "devices" ? await traccar.devices()
    : r === "drivers" ? await traccar.motoristas()
    : r === "geofences" ? await traccar.geofences()
    : r === "maintenance" ? await traccar.manutencoes()
    : r === "groups" ? [
        { id: 1, name: "Frota pesada", groupId: 0, attributes: {} },
        { id: 2, name: "Entregas urbanas", groupId: 0, attributes: {} }]
    : r === "users" ? [
        { id: 1, name: "Administrador", email: "admin@acme.local", readonly: false,
          administrator: true, disabled: false, deviceLimit: -1, attributes: {} },
        { id: 2, name: "Central de monitoramento", email: "central@acme.local",
          readonly: true, administrator: false, disabled: false, deviceLimit: -1,
          attributes: {} }]
    : r === "notifications" ? [
        { id: 1, type: "deviceOverspeed", always: true, notificators: "web,mail",
          calendarId: 0, commandId: 0, attributes: {} },
        { id: 2, type: "alarm", always: false, notificators: "web",
          calendarId: 0, commandId: 0, attributes: {} }]
    : r === "commands" ? [
        { id: 1, description: "Bloquear motor", type: "engineStop",
          textChannel: false, attributes: {} }]
    : r === "computed" ? [
        { id: 1, description: "Combustível em litros", attribute: "fuelLitros",
          expression: "fuel1 * 0.6", type: "number", attributes: {} }]
    : [];
  loja.set(r, inicial as Entidade[]);
  return loja.get(r)!;
}

async function mockListar<T>(r: Recurso): Promise<T[]> {
  return [...(await semear(r))] as T[];
}
async function mockVinculados<T>(r: Recurso, deviceId: Id): Promise<T[]> {
  const todos = await semear(r);
  const set = vinculos.get(`${r}:${deviceId}`);
  if (!set) return (r === "devices" ? [] : todos.slice(0, 1)) as T[];  // algo já ligado
  return todos.filter(i => set.has((i as { id: Id }).id)) as T[];
}
async function mockCriar<T extends { id: Id }>(r: Recurso, item: Omit<T, "id">): Promise<T> {
  const novo = { ...item, id: ++proximoId } as T;
  (await semear(r)).push(novo as unknown as Entidade);
  return novo;
}
async function mockSalvar<T extends { id: Id }>(r: Recurso, item: T): Promise<T> {
  const lista = await semear(r);
  const i = lista.findIndex(x => (x as { id: Id }).id === item.id);
  if (i >= 0) lista[i] = item as unknown as Entidade;
  return item;
}
async function mockExcluir(r: Recurso, id: Id): Promise<void> {
  const lista = await semear(r);
  const i = lista.findIndex(x => (x as { id: Id }).id === id);
  if (i >= 0) lista.splice(i, 1);
}
/** Modo de exemplo: aplica a permission sem servidor. */
async function mockPermissao(dono: Dono, objeto: Objeto, ligar: boolean) {
  const chave = `${objeto.recurso}:${dono.id}`;
  if (!vinculos.has(chave)) {
    const atuais = await mockVinculados<{ id: Id }>(objeto.recurso, dono.id);
    vinculos.set(chave, new Set(atuais.map(a => a.id)));
  }
  const set = vinculos.get(chave)!;
  if (ligar) set.add(objeto.id); else set.delete(objeto.id);
}

const MOCK_TIPOS_EVENTO = [
  "deviceOnline", "deviceOffline", "deviceMoving", "deviceStopped",
  "deviceOverspeed", "deviceFuelDrop", "deviceFuelIncrease", "geofenceEnter",
  "geofenceExit", "alarm", "ignitionOn", "ignitionOff", "maintenance",
  "commandResult", "driverChanged",
];
